import { DateTime } from "luxon";

import {
  generateRecurrenceRuleString,
  FREQUENCIES,
} from "../../../../Recurrence";
import Account from "../../../../Account";
import Filters from "../../../../filters";
import {
  EXPEDITE_CHOICE_MAPPER,
  resolveExpediteChoice,
} from "../bulkPayments/expedite";

import { DisplayStatus } from "./consts";

import type { BulkTemplateForm } from "./BulkTemplate.form";
import type {
  FormattedBulkTemplate,
  FormattedBulkTemplateVersion,
} from "./types";
import type { BulkPaymentRowType } from "../bulkPayments/BulkPayment.form";
import type { ExpediteOption } from "../bulkPayments/expedite";

export const formatBulkTemplate = (
  template: API.BulkTemplate,
  accounts: API.AnyAccount[] = [],
): FormattedBulkTemplate => {
  const {
    id: uuid,
    name: groupName,
    state,
    last_used: lastUsed,
    updated_at: updatedAt,
    active_version: activeVersion,
    pending_version: pendingVersion,
  } = template;

  // for active_pending state, show the active version (not the pending one)
  // since users want to see the currently live template in the table/drawer
  const version =
    state === "active_pending"
      ? activeVersion
      : (pendingVersion ?? activeVersion);
  const name = version?.name ?? groupName;

  const {
    version_number: versionNumber = null,
    institution_account: institutionAccountId = null,
    sec_code: secCode = null,
    entry_desc: entryDesc = null,
    description = null,
    max_expedite_choice: maxExpediteChoice = 0,
    group_posting: groupPosting = false,
    payment_data: paymentData = [],
    dual_approval_state: dualApprovalState = null,
    user_full_name: updatedBy = null,
  } = version ?? {};

  const institutionAccountRaw = accounts.find(
    (a) => a.id === institutionAccountId,
  );
  const institutionAccount = institutionAccountRaw
    ? Account.deserialize(institutionAccountRaw)
    : null;

  const paymentCount = paymentData.length;

  const creditPayments = paymentData.filter(
    (p) => p.transaction_type === "credit",
  );
  const debitPayments = paymentData.filter(
    (p) => p.transaction_type === "debit",
  );

  const creditTotalCents = creditPayments.reduce((sum, p) => sum + p.amount, 0);
  const debitTotalCents = debitPayments.reduce((sum, p) => sum + p.amount, 0);

  const creditTotalDollars =
    creditPayments.length > 0
      ? Filters.currency(creditTotalCents as Cents, { hasDecimal: false })
      : null;
  const debitTotalDollars =
    debitPayments.length > 0
      ? Filters.currency(debitTotalCents as Cents, { hasDecimal: false })
      : null;

  const displayStatus =
    DisplayStatus[state as keyof typeof DisplayStatus] ?? state;

  return {
    uuid,
    name,
    state,
    activeVersionId: activeVersion?.id ?? null,
    lastUsed,
    updatedAt,
    updatedBy,
    displayStatus,
    versionNumber,
    institutionAccount,
    secCode,
    entryDesc,
    description,
    maxExpediteChoice,
    groupPosting,
    paymentCount,
    paymentCreditCount: creditPayments.length,
    paymentDebitCount: debitPayments.length,
    creditTotalDollars,
    debitTotalDollars,
    dualApprovalState,
    pendingDualApprovalId: pendingVersion?.dual_approval_id ?? null,
    paymentData,
  };
};

export const formatBulkTemplateVersion = (
  version: API.BulkTemplateVersion,
): FormattedBulkTemplateVersion => {
  const {
    id,
    name,
    version_number: versionNumber,
    created_at: createdAt,
    user_full_name: userFullName,
    user_email: userEmail,
    dual_approval_state: dualApprovalState,
    dual_approval_id: dualApprovalId = null,
    user_change_log: userChangeLog = null,
    payment_data: paymentData,
  } = version;

  return {
    id,
    name,
    versionNumber,
    createdAt,
    userFullName,
    userEmail,
    dualApprovalState,
    dualApprovalId,
    userChangeLog,
    paymentData,
  };
};

type FeatureFlagOptions = {
  expeditedPaymentsFeature: boolean;
  achPaymentsCollectFeature: boolean;
};

export const formatFormValues = (
  values: ReturnType<typeof BulkTemplateForm.useForm>["values"],
  { expeditedPaymentsFeature, achPaymentsCollectFeature }: FeatureFlagOptions,
): API.CreateBulkTemplate.Request => {
  const {
    title,
    fromAccount,
    secCode,
    description,
    entryDesc,
    groupPosting,
    allowExpedite,
    payments,
    memo,
  } = values;

  const paymentData = payments.map((payment, index) => ({
    recipient: payment.recipient?.id,
    // only include transaction_type when collect feature is enabled,
    ...(achPaymentsCollectFeature && {
      transaction_type: (payment.transactionType === "collect"
        ? "debit"
        : "credit") as "credit" | "debit",
    }),
    amount: payment.amount,
    description: payment.description,
    // expedite_choice is only set when expedited_payments feature is enabled and allowExpedite is turned on
    ...(expeditedPaymentsFeature &&
      allowExpedite &&
      payment.speedStatus && {
        expedite_choice:
          EXPEDITE_CHOICE_MAPPER[
            payment.speedStatus as keyof typeof EXPEDITE_CHOICE_MAPPER
          ],
      }),
    order: index,
  }));

  return {
    name: title,
    version: {
      institution_account: fromAccount as API.AccountId,
      sec_code: secCode || null,
      entry_desc: entryDesc || null,
      description: description ?? "",
      group_posting: groupPosting,
      ...(expeditedPaymentsFeature && {
        max_expedite_choice: allowExpedite ? 2 : 0,
      }),
      ...(memo && { note: memo }),
      payment_data: paymentData,
    },
  };
};

type FormatFromTemplateOptions = {
  template: FormattedBulkTemplate;
  title: string;
  description?: string;
  secCode: string;
  entryDesc: string;
  allPayments: BulkPaymentRowType[];
  excludedRows: Set<number>;
  expeditedPaymentsFeature: boolean;
  chosenExpediteOption?: ExpediteOption;
  deviceId?: string;
  deviceRequestId?: string;
};

export const formatFromTemplatePayload = ({
  template,
  title,
  description,
  secCode,
  entryDesc,
  allPayments,
  excludedRows,
  expeditedPaymentsFeature,
  chosenExpediteOption,
  deviceId,
  deviceRequestId,
}: FormatFromTemplateOptions): API.CreateBulkPaymentFromTemplate.Request => {
  const paymentDataOverrides: Record<
    string,
    API.CreateBulkPaymentFromTemplate.PaymentDataOverride
  > = {};

  const sortedPaymentData = [...template.paymentData].sort(
    (a, b) => a.order - b.order,
  );

  // match by template row id where the form carries it, so an override can
  // never land on the wrong recipient if the two arrays drift out of step
  const formIndexByTemplateRowId = new Map<string, number>();
  allPayments.forEach((payment, index) => {
    if (payment.templateRowId) {
      formIndexByTemplateRowId.set(payment.templateRowId, index);
    }
  });

  sortedPaymentData.forEach((payment, index) => {
    const formIndex = formIndexByTemplateRowId.get(payment.id) ?? index;
    const formPayment = allPayments[formIndex];
    const isExcluded = excludedRows.has(formIndex);

    // the server treats a row missing from payment_data_overrides as included
    // with template defaults, so send an explicit entry for every row
    const override: API.CreateBulkPaymentFromTemplate.PaymentDataOverride =
      isExcluded ? { include: false } : { include: true };

    if (!isExcluded && formPayment) {
      if (formPayment.amount !== payment.amount) {
        override.amount = formPayment.amount;
      }
      if (formPayment.description !== payment.description) {
        override.description = formPayment.description;
      }
      if (formPayment.scheduledDate) {
        const formattedDate = DateTime.fromFormat(
          formPayment.scheduledDate,
          "M/d/yyyy",
        ).toFormat("yyyy-MM-dd");
        const rrule = generateRecurrenceRuleString(
          FREQUENCIES.ONCE,
          formattedDate,
          null,
          null,
        );
        if (rrule) {
          override.recurring_rule = rrule;
        }
      }
      if (expeditedPaymentsFeature) {
        // intent, per-row eligibility, and the template ceiling are all
        // ceilings on one scale — take the lowest
        override.expedite_choice = resolveExpediteChoice({
          chosenExpediteOption,
          speedStatus: formPayment.speedStatus,
          maxExpediteChoice: template.maxExpediteChoice,
        });
      }
    }

    paymentDataOverrides[payment.id] = override;
  });

  return {
    bulk_payment_template: template.uuid,
    active_version_id: template.activeVersionId as API.BulkTemplateVersionId,
    title,
    ...(description && { description }),
    ...(secCode && { sec_code: secCode }),
    ...(entryDesc && { entry_desc: entryDesc }),
    ...(Object.keys(paymentDataOverrides).length > 0 && {
      payment_data_overrides: paymentDataOverrides,
    }),
    ...(deviceId && { device_id: deviceId }),
    ...(deviceRequestId && { device_request_id: deviceRequestId }),
  };
};
